/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
import { getCompletion } from '@/api/characteristics';
import { useCallback, useContext, useEffect, useState } from 'react';

import { CompletionData } from '../../../api/characteristics/types';
import { Status, StatusCode } from '../../../api/enumerations';
import { getSelectedInspectionPictures } from '../../../api/inspections';
import { selectInspectionPictures } from '../../../api/pictures';
import { GalleryPictureData } from '../../../api/pictures/types';
import { RoomData } from '../../../api/rooms/types';
import { getSample } from '../../../api/samples';
import { ElementData } from '../../../api/samples/types';
import {
  addGoogleMapsImagesWithPins,
  generateReportPDF,
  sendGraphs,
} from '../../../api/workOrders';
import {
  AvmFactorsData,
  AvmInferenceData,
  WorkOrderData,
} from '../../../api/workOrders/types';
import { factorTypes } from '../../../constants/evaluationTypes';
import { GlobalContext } from '../../../context/global';
import { isPlaceholderSelectOption } from '../../../helpers';
import { useChangeStatus } from '../../../hooks/useChangeStatus';
import useErrorMessage from '../../../hooks/useErrorMessage';
import useGeneral from '../../../hooks/useGeneral';
import { usePropertyData } from '../../../hooks/usePropertyData';
import { useRooms } from '../../../hooks/useRooms';

interface SendGraphProps {
  title: string;
  subtitle?: string[] | [];
}

type LatLngLiteral = google.maps.LatLngLiteral;

interface ICompleteSections {
  additionalFields: boolean;
  inspectionImages: boolean;
  propertyImages: boolean;
}
interface ReportHook {
  propertyData: WorkOrderData | undefined;
  sampleData: ElementData[] | undefined;
  hasAvmReport: boolean;
  rooms: RoomData[];
  selectedPictures: GalleryPictureData[];
  getCharts: (img: HTMLCanvasElement, title: string) => Promise<void>;
  handleChangeStatus: () => Promise<void>;
  removeSelectedPictures: (id: number) => Promise<void>;
  forceFactors: string[];
  forceTransformations: string[];
  loadingApprove: boolean;
  loadingPage: boolean;
  avmStatus: string;
  getDataCallback: () => Promise<void>;
  completedSections: ICompleteSections;
  updateAdditionalFields: () => void;
  openAttchamentsDialog: boolean;
  onOpenAttachments: () => void;
  onCloseAttachments: () => void;
  completionData: CompletionData | undefined;
  loadingCompletion: boolean;
  isLegacyCharacteristicsData: boolean;
}

const useReport = (): ReportHook => {
  const [files, setFiles] = useState<File[]>([]);
  const [graphsLength, setGraphsLength] = useState<number>(1);
  const [hasGraphs, setHasGraphs] = useState(false);
  const [hasAvmReport, setHasAvmReport] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [metadata, setMetadata] = useState<SendGraphProps[]>([]);
  const [forceFactors, setForceFactors] = useState<string[]>([]);
  const [forceTransformations, setForceTransformations] = useState<string[]>(
    []
  );
  const [selectedPictures, setSelectedPictures] = useState<
    GalleryPictureData[]
  >([]);
  const [pinPlace, setPinPlace] = useState<LatLngLiteral>();
  const [avmStatus, setAvmStatus] = useState('');
  const [sampleData, setSampleData] = useState<ElementData[]>();
  const [updatedAdditionaFields, setUpdatedAdditionalFields] = useState(false);
  const [openAttchamentsDialog, setOpenAttachmentsDialog] = useState(false);
  const [completionData, setCompletionData] = useState<
    CompletionData | undefined
  >();
  const [loadingCompletion, setLoadingCompletion] = useState(false);
  const [completedSections, setCompletedSections] = useState<ICompleteSections>(
    {
      propertyImages: false,
      additionalFields: false,
      inspectionImages: false,
    }
  );

  const { handleStatus, loadingApprove, setLoadingApprove } = useChangeStatus();
  const { osId } = useGeneral();
  const { propertyData, getDataCallback } = usePropertyData({
    status: Status.REPORT,
  });
  const { handleRooms, rooms } = useRooms();
  const { getErrorMessage } = useErrorMessage();
  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage, isHiveOwner } =
    useContext(GlobalContext);

  const onOpenAttachments = (): void => setOpenAttachmentsDialog(true);
  const onCloseAttachments = (): void => setOpenAttachmentsDialog(false);

  const getCharts = useCallback(
    async (img: HTMLCanvasElement, title: string): Promise<void> => {
      if (hasGraphs) return;

      const subtitles = [
        'Regressão',
        'Intervalo de predição',
        'Intervalo de confiança',
      ];
      const substring1 = ['Gráfico de Aderência -'];
      const substring2 = ['Privativa', 'Garagem', 'Cub', 'IBGE'];
      let newGraphMetadata: SendGraphProps;

      img.toBlob((blob) => {
        if (blob) {
          const imageFile = new File([blob], title, { type: 'image/png' });

          newGraphMetadata = { title };

          const containOneSubtitle = substring1.some((sub1) =>
            title.includes(sub1)
          );

          const containThreeSubtitles = substring2.some((substring) =>
            title.includes(substring)
          );
          if (containOneSubtitle) newGraphMetadata.subtitle = ['Bissetriz'];
          if (containThreeSubtitles) newGraphMetadata.subtitle = subtitles;

          setFiles((prevFiles) => [...prevFiles, imageFile]);
          setMetadata((prevMetadata) => [...prevMetadata, newGraphMetadata]);
        }
      });
    },
    [files]
  );

  const getSelectedPictures = async (inspectionId: number): Promise<void> => {
    try {
      const response = await getSelectedInspectionPictures(inspectionId);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSelectedPictures(response.data);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    }
  };

  const getSamples = async (): Promise<void> => {
    let sample = false;
    let isFactors = false;

    if (propertyData) {
      if (propertyData.force_factors) {
        setForceFactors(propertyData.force_factors);
      }
      if (propertyData.force_transformations) {
        setForceTransformations(propertyData.force_transformations);
      }
      if (propertyData.inspection) {
        handleRooms(propertyData.inspection.id);
        getSelectedPictures(propertyData.inspection.id);
      }
      const avmData = propertyData.avm_report as
        | AvmInferenceData
        | AvmFactorsData
        | null;

      if (avmData && avmData.scores) setHasAvmReport(true);

      if (factorTypes.includes(propertyData.evaluation_type)) {
        isFactors = true;
      } else if (avmData) {
        const avmInference = avmData as AvmInferenceData;
        setGraphsLength(5 + avmInference.independent_variables_plots.length);
      }

      if (propertyData.latitude && propertyData.longitude) {
        setPinPlace({
          lat: Number(propertyData.latitude),
          lng: Number(propertyData.longitude),
        });
      }

      if (propertyData.avm_status?.failure_reason) {
        setAvmStatus(propertyData.avm_status.failure_reason);
      }

      if (propertyData.samples) sample = true;
      setHasGraphs(propertyData.has_graphs);
      setUpdatedAdditionalFields(!updatedAdditionaFields);
      setLoadingPage(false);
    }

    if (sample) {
      try {
        const response = await getSample(osId, 1, 105);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (!response.data) {
          throw new Error('A amostra não pode ser carregada.');
        }

        if (isFactors) {
          const filteredSamples: ElementData[] = response.data.items.filter(
            (data) => data.is_utilized
          );
          setSampleData(filteredSamples);
          return;
        }
        setSampleData(response.data.items);
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      }
    }
  };

  useEffect(() => {
    getSamples();
  }, [propertyData]);

  const removeSelectedPictures = async (id: number): Promise<void> => {
    if (!propertyData?.inspection?.id) return;

    const updatePictures = selectedPictures.filter((photo) => photo.id !== id);

    const formatted = updatePictures.map((pic) => {
      return { id: pic.id, selected_index: pic.selected_index };
    });

    const selectedPicturesData = {
      inspection_id: propertyData.inspection.id,
      selected_pictures: formatted,
    };
    try {
      const response = await selectInspectionPictures(selectedPicturesData);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.detail.status) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSelectedPictures(updatePictures);
      setSnackbarMessage('Imagem removida com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  const handleReportGraphs = useCallback(async (): Promise<void> => {
    if (hasGraphs) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i += 1) {
      formData.append('files', files[i]);
      formData.append('metadata', JSON.stringify(metadata[i]));
    }

    try {
      const response = await sendGraphs(osId, formData);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== 0) {
        throw new Error(
          'Gráficos não foram salvos, carregue a página novamente.'
        );
      }

      setHasGraphs(true);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    }
  }, [files]);

  useEffect(() => {
    if (propertyData && files.length === graphsLength) {
      handleReportGraphs();
    }
  }, [files, graphsLength]);

  const updateGoogleMap = useCallback(
    async (
      osPin: LatLngLiteral,
      samplePins: ElementData[],
      evaluationType: 'factors' | 'inferences'
    ) => {
      const mapSize = evaluationType === 'factors' ? '460x390' : '580x480';

      const formatMarkers = (sample: ElementData[]): string =>
        sample
          .map(
            (element) =>
              `${element.description.latitude}, ${element.description.longitude} |`
          )
          .join(' ');

      const usedElements = samplePins.filter((element) => element.is_utilized);
      const unusedElements = samplePins.filter(
        (element) => !element.is_utilized
      );
      const usedMarkers = formatMarkers(usedElements);
      const unusedMarkers = formatMarkers(unusedElements);

      const baseUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${osPin.lat},${osPin.lng}&style=feature:poi|element:labels|visibility:off&zoom=14&size=${mapSize}&scale=2`;
      const mapType = '&maptype=satellite';
      const workOrderMarker = `&markers=color:0xe67615|${osPin.lat},${osPin.lng}&markers=color:0xae38f9|size:tiny|`;
      const unusedMarkersString =
        evaluationType === 'inferences'
          ? `&markers=color:0x78909C|size:tiny|${unusedMarkers}`
          : '';
      const apiKey = `&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

      const mapFile = await fetch(
        `${baseUrl}${workOrderMarker}${usedMarkers}${unusedMarkersString}${apiKey}`
      )
        .then((r) => r.blob())
        .then(
          (bFile) => new File([bFile], 'map-elements', { type: 'image/png' })
        );

      const mapSatelliteFile = await fetch(
        `${baseUrl}${mapType}${workOrderMarker}${usedMarkers}${unusedMarkersString}${apiKey}`
      )
        .then((r) => r.blob())
        .then(
          (bFile) =>
            new File([bFile], 'satellite-map-elements', { type: 'image/png' })
        );

      const mapImageData = new FormData();
      mapImageData.append('google_maps', mapFile);
      mapImageData.append('satellite_view', mapSatelliteFile);

      try {
        const response = await addGoogleMapsImagesWithPins(osId, mapImageData);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== 0) {
          throw new Error('Algo deu errado, tente novamente.');
        }
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      }
    },
    []
  );

  const handleChangeStatus = async (): Promise<void> => {
    let hasPdf = false;
    setLoadingApprove(true);

    if (propertyData?.avm_report) {
      const { dropdown } = propertyData.avm_report.descriptions;
      if (
        !dropdown ||
        dropdown.performance.chosen === '' ||
        dropdown.number_of_offers.chosen === '' ||
        dropdown.liquidity.chosen === ''
      ) {
        setSnackbarMessage('Os dados de seleção são obrigatórios');
        setOpenSnackbar(true);
        setErrorMessage(true);
        setLoadingApprove(false);
        return;
      }
    }

    if (propertyData && pinPlace && sampleData) {
      const isFactorsEvaluation = factorTypes.includes(
        propertyData.evaluation_type
      );

      const evaluationType = isFactorsEvaluation ? 'factors' : 'inferences';
      updateGoogleMap(pinPlace, sampleData, evaluationType);
    }

    try {
      const response = await generateReportPDF(osId);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      hasPdf = true;
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
      setLoadingApprove(false);
    }

    if (hasPdf) {
      handleStatus(osId, propertyData?.reference_number);
    }
  };

  const updateAdditionalFields = (): void =>
    setUpdatedAdditionalFields(!updatedAdditionaFields);

  const getCharacteristicsCompletion = async (): Promise<void> => {
    setLoadingCompletion(true);
    try {
      const response = await getCompletion(osId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setCompletionData(response.data);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    } finally {
      setLoadingCompletion(false);
    }
  };

  useEffect(() => {
    if (propertyData && propertyData.hive_request !== null)
      getCharacteristicsCompletion();
  }, [propertyData]);

  const characteristicsForm = propertyData?.property_characteristics_form;
  const isLegacyCharacteristicsData = characteristicsForm
    ? 'uses' in (characteristicsForm as unknown as Record<string, unknown>)
    : false;

  useEffect(() => {
    if (!isHiveOwner) {
      setCompletedSections((prev) => ({ ...prev, propertyImages: true }));
      return;
    }
    const isFactor =
      propertyData && factorTypes.includes(propertyData.evaluation_type);

    if (isFactor && propertyData.facade_image) {
      setCompletedSections((prev) => ({ ...prev, propertyImages: true }));
    } else if (!isFactor && rooms.length > 0) {
      setCompletedSections((prev) => ({ ...prev, propertyImages: true }));
    } else {
      setCompletedSections((prev) => ({ ...prev, propertyImages: false }));
    }
  }, [isHiveOwner, propertyData, rooms]);

  useEffect(() => {
    if (propertyData?.avm_report) {
      const { dropdown } = propertyData.avm_report.descriptions;
      const requiredFields = [
        dropdown.performance.chosen,
        dropdown.number_of_offers.chosen,
        dropdown.liquidity.chosen,
      ];
      if (requiredFields.some(isPlaceholderSelectOption)) {
        setCompletedSections((prev) => ({ ...prev, additionalFields: false }));
      } else {
        setCompletedSections((prev) => ({
          ...prev,
          additionalFields: true,
        }));
      }
    }
  }, [propertyData, updatedAdditionaFields]);

  useEffect(() => {
    if (rooms.length > 0 && selectedPictures.length > 0) {
      setCompletedSections((prev) => ({ ...prev, inspectionImages: true }));
    } else if (rooms.length === 0) {
      setCompletedSections((prev) => ({ ...prev, inspectionImages: true }));
    } else {
      setCompletedSections((prev) => ({ ...prev, inspectionImages: false }));
    }
  }, [selectedPictures]);

  return {
    propertyData,
    selectedPictures,
    removeSelectedPictures,
    getCharts,
    handleChangeStatus,
    loadingPage,
    avmStatus,
    forceFactors,
    forceTransformations,
    hasAvmReport,
    loadingApprove,
    rooms,
    sampleData,
    getDataCallback,
    completedSections,
    updateAdditionalFields,
    openAttchamentsDialog,
    onOpenAttachments,
    onCloseAttachments,
    completionData,
    loadingCompletion,
    isLegacyCharacteristicsData,
  };
};

export default useReport;
